import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search } from "lucide-react";
import { http } from "@/lib/api";
import { Reveal, SectionLabel } from "./Reveal";
import { ProductCard } from "./ProductCard";
import { ProductModal } from "./ProductModal";


export const Catalog = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    http.get("/products")
      .then((r) => setProducts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFailed(true));
    http.get("/categories").then((r) => setCategories(Array.isArray(r.data) ? r.data : [])).catch(() => null);
  }, []);

  const labels = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c.label])), [categories]);
  const cats = useMemo(() => [{ id: "all", label: "Tutti" }, ...categories], [categories]);
  useEffect(() => {
    if (cat !== "all" && categories.length && !cats.some((c) => c.id === cat)) setCat("all");
  }, [cats, cat, categories.length]);

  const shown = useMemo(() => products.filter((p) =>
    (cat === "all" || p.category === cat) && p.name?.toLowerCase().includes(q.trim().toLowerCase())), [products, cat, q]);

  return (
    <section id="catalogo" className="mx-auto max-w-7xl px-5 py-28 sm:px-8">
      <Reveal className="mb-12 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <div>
          <SectionLabel index="01">Catalogo</SectionLabel>
          <h2 className="mt-5 font-display text-4xl font-semibold leading-[0.95] tracking-tight sm:text-6xl">
            Scelti con cura.<br /><span className="text-outline">Pronti da spedire.</span>
          </h2>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex flex-wrap gap-1 rounded-full border border-fg/10 bg-[var(--ink-2)] p-1">
            {cats.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} data-testid={`catalog-category-${c.id}`}
                className="relative rounded-full px-4 py-2 text-sm transition-colors">
                {cat === c.id && <motion.span layoutId="cat-pill" className="absolute inset-0 rounded-full bg-[var(--paper)]" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
                <span className={`relative ${cat === c.id ? "text-[var(--ink)]" : "text-fg2"}`}>{c.label}</span>
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 rounded-full border border-fg/10 bg-[var(--ink-2)] px-4 py-2.5 focus-within:border-[var(--volt)]/50">
            <Search className="h-4 w-4 text-mist" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un modello" data-testid="catalog-search-input"
              className="w-full bg-transparent text-sm outline-none placeholder:text-mist sm:w-40" />
          </label>
        </div>
      </Reveal>
      {failed && <p className="text-mist" data-testid="catalog-error">Il catalogo non è disponibile in questo momento. Riprova più tardi.</p>}
      <motion.div layout className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <AnimatePresence mode="popLayout">
          {shown.map((p) => <ProductCard key={p.id} p={{ ...p, categoryLabel: labels[p.category] }} onDetails={setSelected} />)}
        </AnimatePresence>
      </motion.div>
      {!failed && products.length > 0 && shown.length === 0 && (
        <p className="py-16 text-center text-mist" data-testid="catalog-empty">
          {q.trim() ? "Nessun prodotto trovato." : "Presto nuovi prodotti in questa categoria. Scrivici per una richiesta su misura."}
        </p>
      )}
      <ProductModal product={selected} onClose={() => setSelected(null)} />
    </section>
  );
};
